import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { postApi } from "@/lib/api";
import { Container, PageHeader } from "@/components/site/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NewsletterForm } from "@/components/site/NewsletterForm";

const SUBJECTS = ["Proposer un concert / un événement", "Envoyer un son pour les arrivages", "Artiste : présenter mon projet", "Salle / organisateur : partenariat", "Autre"];
const field = "rounded-none bg-transparent border-white/20 h-14 text-base focus-visible:ring-primary";
const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject) return toast.error("Choisis un sujet.");
    setLoading(true);
    try {
      await postApi("/contact", form);
      toast.success("Message envoyé ! On revient vers toi très vite.");
      setForm(EMPTY);
    } catch {
      toast.error("Vérifie les champs du formulaire.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader top="Contact" bottom="écris-nous" testId="contact-header"
        intro="Fan, artiste, salle ou organisateur : un concert à annoncer, un son à faire tourner sur la radio, un partenariat ? Écris-nous." />
      <Container className="py-20 grid lg:grid-cols-[1.3fr_1fr] gap-16">
        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Input required placeholder="Nom" value={form.name} onChange={set("name")} className={field} data-testid="contact-name-input" />
            <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" />
          </div>
          <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
            <SelectTrigger className={field} data-testid="contact-subject-select"><SelectValue placeholder="Sujet" /></SelectTrigger>
            <SelectContent className="rounded-none bg-black border-white/10">
              {SUBJECTS.map((s, i) => <SelectItem key={s} value={s} data-testid={`contact-subject-${i}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea required placeholder="Ton message" value={form.message} onChange={set("message")} className="rounded-none bg-transparent border-white/20 min-h-[200px] text-base focus-visible:ring-primary" data-testid="contact-message-input" />
          <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="inline-flex items-center gap-3 bg-primary text-white font-display uppercase text-xl px-10 py-5 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Envoyer
          </button>
        </form>
        <aside className="bg-[hsl(var(--surface))] border border-white/10 p-8 md:p-12 self-start">
          <h2 className="font-display uppercase text-5xl leading-[0.9] text-white">La<br /><span className="text-primary">newsletter</span></h2>
          <p className="text-zinc-400 mt-6 mb-8">Les concerts, les arrivages et les nouvelles émissions, une fois de temps en temps.</p>
          <NewsletterForm />
        </aside>
      </Container>
    </>
  );
}
